/**
 * File: LegalResearch.jsx
 * Path: /src/pages/LegalResearch.jsx
 * Description: Dedicated page showcasing Veritect.ai's Legal Research AI solution
 * Key Features: Interactive AI visualization, feature breakdown, and benefits section
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LegalResearchDashboard from '../components/solutions/LegalResearchDashboard';
import LegalResearchMemoDashboard from '../components/solutions/LegalResearchMemoDashboard';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const LegalResearch = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Features data
  const features = [
    {
      title: "Natural Language Query",
      description: "Ask complex legal questions in plain English and receive precise answers with supporting cases and statutes.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      )
    },
    {
      title: "AI-Powered Relevance",
      description: "Our machine learning algorithms understand context and relationships between legal concepts to deliver the most relevant results.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      title: "Brief & Memo Generation",
      description: "Automatically generate first drafts of legal briefs and memos that incorporate your research findings.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Jurisdiction Filtering",
      description: "Narrow your search to specific jurisdictions, practice areas, or time periods to find precisely what you need.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      )
    },
    {
      title: "Citation Validation",
      description: "Automatically verify the validity of citations and receive alerts for overturned cases or superseded statutes.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: "Integration Ecosystem",
      description: "Seamlessly connects with major legal research platforms and practice management software for a unified workflow.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  // How it works steps
  const workflowSteps = [
    {
      number: "01",
      title: "Ask Your Question",
      description: "Type your legal query in natural language, just as you'd ask a colleague."
    },
    {
      number: "02",
      title: "AI-Powered Analysis",
      description: "Our system analyzes millions of cases, statutes, and legal documents in seconds."
    },
    {
      number: "03",
      title: "Review Results",
      description: "Receive organized, relevant results with summaries and key passages highlighted."
    },
    {
      number: "04",
      title: "Generate Documents",
      description: "Create briefs or memos incorporating the research with a single click."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-primary-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-secondary-100 rounded-tr-full opacity-70"></div>
        
        {/* Floating decorative elements */}
        <motion.div 
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{ 
            y: [0, -15, 0], 
            opacity: [0.4, 0.7, 0.4] 
          }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
        >
          <div className="w-16 h-16 border-2 border-primary-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div 
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{ 
            y: [0, 15, 0], 
            opacity: [0.3, 0.6, 0.3] 
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity, 
            repeatType: "reverse", 
            delay: 1 
          }}
        >
          <div className="w-12 h-12 border-2 border-accent-500/30 rounded-full"></div>
        </motion.div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  AI-Powered <span className="gradient-text">Legal Research</span> Platform
                </h1>
                <p className="text-xl text-dark-600 mb-8">
                  Find relevant cases, statutes, and legal doctrines in seconds, not hours. Our AI understands context and identifies connections human researchers might miss.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-10">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/contact"
                    className="shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Request a Demo</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    to="/solutions/legal-research#how-it-works"
                  >
                    See How It Works
                  </Button>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex -space-x-2">
                    {['#FFBB5C', '#FF9B50', '#E25E3E', '#C63D2F'].map((color, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, zIndex: 4-i }}>
                        {['J', 'S', 'M', 'R'][i]}
                      </div>
                    ))}
                  </div>
                  <div className="text-dark-500">
                    <strong className="text-dark-700">500+</strong> law firms using our platform
                  </div>
                </div>
              </motion.div>
              
              {/* Right Side - Interactive Visualization */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {/* Legal Research Dashboard Component */}
                <LegalResearchDashboard />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <ModernStatsSection 
        title="Transform Your Legal Research"
        description="See the real impact our AI-powered legal research platform is making for law firms."
        stats={[
          { number: '85%', label: 'Time Saved on Research' },
          { number: '3.5x', label: 'More Cases Analyzed' },
          { number: '92%', label: 'Accuracy Rate' },
          { number: '74%', label: 'More Relevant Citations' }
        ]} 
      />
      
      {/* Features Section */}
      <section className="py-20 bg-white" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Key Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Powerful Research Capabilities
            </h2>
            <p className="text-dark-500 text-lg">
              Our legal research platform combines cutting-edge AI with deep legal expertise 
              to deliver a research experience that's faster, more comprehensive, and more insightful.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full flex flex-col items-start">
                  <div className="rounded-lg p-3 bg-primary-50 text-primary-600 mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-dark-500">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-secondary-600/5 rounded-full blur-3xl"></div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Legal Research, Reimagined
            </h2>
            <p className="text-dark-500 text-lg">
              Our intuitive workflow makes legal research faster and more effective than ever before.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                {/* Connector line between steps (hidden on mobile) */}
                {index < workflowSteps.length - 1 && (
                  <div className="absolute hidden lg:block top-6 left-full w-full h-0.5 bg-primary-200 z-0">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45">
                      <svg className="w-3 h-3 text-primary-400" fill="currentColor" viewBox="0 0 256 512">
                        <path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path>
                      </svg>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-600 text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-primary-600 font-semibold">Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Why Choose Veritect Legal Research AI
              </h2>
              <p className="text-dark-600 mb-8">
                Our AI-powered legal research platform doesn't just save time—it transforms how legal professionals
                approach research, enabling deeper insights and better outcomes for clients.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Comprehensive Coverage",
                    description: "Access millions of cases, statutes, regulations, and secondary sources from all jurisdictions in one unified search."
                  },
                  {
                    title: "Contextual Understanding",
                    description: "Our AI understands the meaning behind your query, not just keywords, delivering truly relevant results."
                  },
                  {
                    title: "Time and Cost Efficiency",
                    description: "Reduce research time by up to 85%, allowing attorneys to focus on analysis and strategy."
                  },
                  {
                    title: "Deeper Insights",
                    description: "Discover connections and precedents that traditional research methods might miss."
                  }
                ].map((benefit, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="mr-4 text-primary-600 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{benefit.title}</h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10">
                <Button 
                  variant="primary"
                  to="/contact"
                  className="shadow-lg"
                >
                  Schedule a Demo
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Legal Research Memo Dashboard Component */}
              <LegalResearchMemoDashboard />
              
              {/* Floating elements for visual interest */}
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-accent-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonial Section */}
      <section className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400"></div>
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
            </g>
          </svg>
        </div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Hear From Our Clients
            </h2>
            <p className="text-dark-500 text-lg">
              Leading law firms are transforming their legal research with Veritect.ai
            </p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 relative max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Quote mark */}
            <div className="absolute top-6 left-8 text-primary-300 opacity-30 text-6xl font-serif">
              "
            </div>
            
            <div className="pl-8">
              <blockquote className="text-lg mb-6 text-dark-700">
                Veritect's legal research platform has completely transformed how our associates approach research tasks. 
                What used to take hours of combing through cases now takes minutes, and the AI surfaces connections 
                and precedents we might have missed. The time savings alone has been a game-changer, but the 
                quality of insights is what truly sets it apart.
              </blockquote>
              
              <div className="flex items-center">
                <div className="mr-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center text-white text-lg font-bold">
                    SJ
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800">Sarah Johnson</h4>
                  <p className="text-dark-500 text-sm">Managing Partner, Johnson & Associates</p>
                </div>
                <div className="ml-auto flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Everything you need to know about our Legal Research AI platform
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How accurate is Veritect's Legal Research AI?",
                answer: "Our platform has demonstrated a 92% accuracy rate in identifying relevant cases and statutes, exceeding human-only research in benchmark tests. All AI outputs are designed to be verified by legal professionals, with clear source citations for every reference."
              },
              {
                question: "How does Veritect integrate with existing legal research tools?",
                answer: "Veritect's Legal Research AI integrates seamlessly with major legal research databases including Westlaw, LexisNexis, Bloomberg Law, and Casetext. We also provide APIs for custom integrations with your firm's document management and knowledge systems."
              },
              {
                question: "What jurisdictions does Veritect's platform cover?",
                answer: "Our platform covers all U.S. federal and state jurisdictions, including specialized courts. We also offer international coverage for major common law jurisdictions including the UK, Canada, Australia, and Singapore, with expanding coverage for EU and civil law jurisdictions."
              },
              {
                question: "How long does implementation take?",
                answer: "Most firms are up and running with our Legal Research AI within 1-3 weeks. Implementation includes system setup, customization to your firm's practice areas, and comprehensive training for attorneys and staff. Our customer success team provides dedicated support throughout the process."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Ready to Transform Your Legal Research?"
        description="Join hundreds of forward-thinking law firms using Veritect.ai to gain a competitive edge in legal research."
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
    </motion.div>
  );
};

export default LegalResearch;